import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import WebDatatable from 'app/shared/datatable/web/datatable';
import MobileDatatable from 'app/shared/datatable/mobile/datatable';

import { addLogSearchResultsToStore, addManyLogsToStore } from 'store/actions/log';
import { setPageTitle } from 'store/actions/header';
import { useLogService } from 'hooks/log';
import { Button } from 'app/shared/button';
import GmModal from 'app/shared/modal/modal';
import { LogView } from '../view';

const ListCampaigns = () => {
  const dispatch = useDispatch();
  const { read, search } = useLogService();
  const { is_mobile_view } = useSelector((state) => state.metadata);
  const stored_logs = useSelector((state) => state.logs);

  const [logs, setLogs] = useState([]);
  const [is_search_mode, setSearchMode] = useState(false);
  const [loading, setLoading] = useState(false);

  const [total_logs, setTotalLogs] = useState(0);

  const [show_log, setShowLog] = useState(false);
  const [selected_log, setSelectedLog] = useState({});

  useEffect(() => {
    dispatch(setPageTitle([{ title: 'Logs', path: '.' }]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLogs(Object.values(stored_logs || {}));
  }, [stored_logs]);

  const config = {
    actions: {
      single: ['View', 'Delete']
    },
    allow_bulk_action: true,
    css: {},
    fields: [
      {
        title: 'Type',
        key: 'type',
        isTitle: true,
        formatter: (value) => (value.length > 25 ? `${value.substr(0, 25)}...` : value)
      },
      {
        title: 'Environment',
        key: 'environment',
        isTagline: true
      },
      {
        title: 'Service',
        key: 'service',
        isTagline: true
      },
      {
        title: 'Message',
        key: 'message',
        isTagline: true
      },
      {
        title: 'Creation date',
        key: 'created_on',
        formatter: (value) => {
          const iso_date = new Date(value).toISOString();
          const [good_part] = iso_date.split('.');
          return good_part.replace('T', ' ');
        },
        isMetadata: true
      }
    ],
    is_search_mode,
    items: logs,
    search_key: 'type,message',
    search_text: ''
  };

  const handleDatatableAction = (payload) => {
    const { name, type, data } = payload;

    if (type === 'single') {
      switch (name) {
        case 'Edit':
          handleItemClick(data);
          break;
        default:
          handleItemClick(data);
      }
    }
  };

  const handleItemClick = (payload) => {
    setSelectedLog(payload);
    setShowLog(true);
  };

  const handleDataRequest = async (page, population = 50) => {
    try {
      setLoading(true);
      const { logs, size } = await read({
        query_string: `page=${page}&population=${population}&sort_by=-created_on`
      });

      dispatch(addManyLogsToStore(logs.map((log) => ({ ...log, meta: log.metadata }))));
      setTotalLogs(size);
    } catch (e) {
      dispatch(addManyLogsToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const handleSearchRequest = async (keys, keyword, page, population = 50) => {
    if (!keys) return;
    try {
      setLoading(true);
      const {
        error,
        payload: { data, meta }
      } = await search(keys, keyword, {
        query_string: `page=${page}&population=${population}&sort_by=-created_on`
      });

      if (error) return;

      setTotalLogs(meta.size);
      if (page === 0)
        return dispatch(
          addLogSearchResultsToStore(data.map((log) => ({ ...log, meta: log.metadata })))
        );
      dispatch(addManyLogsToStore(data));
    } catch (e) {
      dispatch(addManyLogsToStore([]));
    } finally {
      setLoading(false);
    }
  };

  const table_actions = (
    <>
      <Button text="Refresh" icon_name="reload" onClick={() => handleDataRequest(0)} />
    </>
  );

  return (
    <div className="content-area">
      {is_mobile_view ? (
        <MobileDatatable
          config={config}
          action={handleDatatableAction}
          onClick={handleItemClick}
          onListModeChange={setSearchMode}
          onDataRequest={handleDataRequest}
          onSearchRequest={handleSearchRequest}
        />
      ) : (
        <WebDatatable
          config={{
            ...config,
            total_count: total_logs
          }}
          action={handleDatatableAction}
          onClick={handleItemClick}
          checkbox
          loading_data={loading}
          table_actions={table_actions}
          onDataRequest={handleDataRequest}
          onSearchRequest={handleSearchRequest}
        />
      )}
      <GmModal
        title={`Log Details: ${selected_log.id || ''}`}
        show_title
        position="right"
        show_modal={show_log}
        onClose={() => setShowLog(false)}
      >
        <LogView log={selected_log} />
      </GmModal>
    </div>
  );
};

export default ListCampaigns;
